import { Grid, Typography } from '@mui/material';
import { FC } from 'react';
import * as yup from 'yup';

import { FormikDialogForm } from '../../../components/formikDialogForm/components/FormikDialogForm';
import { DialogMode } from '../../../components/formikDialogForm/types';
import { FormikSelect } from '../../../components/formikInputs/FormikSelect';
import { FormikTextField } from '../../../components/formikInputs/FormikTextField';
import { useCreateKundeMutation, useUpdateKundeMutation } from '../../kunde/api/kundeApi';
import {
  CreateInteressentDTO,
  InteressentenStatus,
  interessentenStatusOptions,
  KontaktTyp,
  Kunde,
  UpdateKundeDTO,
} from '../../kunde/api/types';

const createInteressentDTO: CreateInteressentDTO = {
  typ: KontaktTyp.INTERESSENT,
  organisation: '',
  operativerAnsprechpartner: null,
  email: null,
  telefonnummer: null,
  quelle: null,
  vertriebsStatus: InteressentenStatus.NEU,
  angebotsnummer: null,
  angebotslink: null,
  bemerkung: null,
  zuletztBearbeitet: null,
  stadt: null,
  postleitzahl: null,
  strasse: null,
  hausnummer: null,
  adresszusatz: null,
};

const interessentValidationSchema = yup.object({
  organisation: yup.string().required('erforderlich'),
  email: yup.string().email('Ungültige E-Mail-Adresse').nullable().notRequired(),
  operativerAnsprechpartner: yup.string().nullable().notRequired(),
  telefonnummer: yup.string().nullable().notRequired(),
  quelle: yup.string().nullable().notRequired(),
  vertriebsStatus: yup.string().required('erforderlich'),
  angebotsnummer: yup.string().nullable().notRequired(),
  angebotslink: yup.string().url('Ungültige URL').nullable().notRequired(),
  bemerkung: yup.string().nullable().notRequired(),
  stadt: yup.string().nullable().notRequired(),
  postleitzahl: yup.string().nullable().notRequired(),
  strasse: yup.string().nullable().notRequired(),
  hausnummer: yup.string().nullable().notRequired(),
  adresszusatz: yup.string().nullable().notRequired(),
});

export const InteressentDialogForm: FC = () => {
  const [createKunde, { isLoading: createLoading }] = useCreateKundeMutation();
  const [updateKunde, { isLoading: updateLoading }] = useUpdateKundeMutation();

  const saveInteressent = (
    dto: CreateInteressentDTO | UpdateKundeDTO,
    mode: DialogMode
  ): Promise<Kunde> => {
    const payload = {
      ...dto,
      typ: KontaktTyp.INTERESSENT,
      zuletztBearbeitet: new Date().toISOString(),
    };

    return mode === 'create'
      ? createKunde(payload as CreateInteressentDTO).unwrap()
      : updateKunde(payload as UpdateKundeDTO).unwrap();
  };

  return (
    <FormikDialogForm
      dialogId="interessent"
      title={mode => (mode === 'create' ? 'Neuer Interessent' : 'Interessent bearbeiten')}
      onSubmit={saveInteressent}
      creationInitialValues={createInteressentDTO}
      validationSchema={interessentValidationSchema}
      loading={createLoading || updateLoading}
      form={
        <div>
          <Typography sx={{ mb: 2 }}>Stammdaten</Typography>
          <Grid container spacing={2}>
            <Grid size={6}>
              <FormikTextField
                name="organisation"
                textFieldProps={{ label: 'Organisation', fullWidth: true, required: true }}
              />
            </Grid>
            <Grid size={6}>
              <FormikTextField
                name="operativerAnsprechpartner"
                textFieldProps={{ label: 'Operativer Ansprechpartner', fullWidth: true }}
              />
            </Grid>
            <Grid size={6}>
              <FormikTextField
                name="strasse"
                textFieldProps={{ label: 'Straße', fullWidth: true }}
              />
            </Grid>
            <Grid size={3}>
              <FormikTextField
                name="hausnummer"
                textFieldProps={{ label: 'Hausnummer', fullWidth: true }}
              />
            </Grid>
            <Grid size={3}>
              <FormikTextField
                name="adresszusatz"
                textFieldProps={{ label: 'Adresszusatz', fullWidth: true }}
              />
            </Grid>
            <Grid size={6}>
              <FormikTextField
                name="postleitzahl"
                textFieldProps={{ label: 'PLZ', fullWidth: true }}
              />
            </Grid>
            <Grid size={6}>
              <FormikTextField name="stadt" textFieldProps={{ label: 'Stadt', fullWidth: true }} />
            </Grid>
            <Grid size={6}>
              <FormikTextField
                name="email"
                textFieldProps={{ label: 'E-Mail', type: 'email', fullWidth: true }}
              />
            </Grid>
            <Grid size={6}>
              <FormikTextField
                name="telefonnummer"
                textFieldProps={{ label: 'Telefonnummer', fullWidth: true }}
              />
            </Grid>
            <Grid size={6}>
              <FormikTextField
                name="quelle"
                textFieldProps={{ label: 'Quelle', fullWidth: true }}
              />
            </Grid>
            <Grid size={6}>
              <FormikSelect
                name="vertriebsStatus"
                label="Status"
                options={[...interessentenStatusOptions]}
                required={true}
              />
            </Grid>
            <Grid size={6}>
              <FormikTextField
                name="angebotsnummer"
                textFieldProps={{ label: 'Angebotsnummer', fullWidth: true }}
              />
            </Grid>
            <Grid size={6}>
              <FormikTextField
                name="angebotslink"
                textFieldProps={{ label: 'Angebotslink', type: 'url', fullWidth: true }}
              />
            </Grid>
            <Grid size={12}>
              <FormikTextField
                name="zuletztBearbeitet"
                textFieldProps={{ label: 'Zuletzt bearbeitet', fullWidth: true, disabled: true }}
              />
            </Grid>
            <Grid size={12}>
              <FormikTextField
                name="bemerkung"
                textFieldProps={{
                  label: 'Bemerkung',
                  fullWidth: true,
                  multiline: true,
                  minRows: 3,
                }}
              />
            </Grid>
          </Grid>
        </div>
      }
    />
  );
};
